import { ROUTES } from 'core/constants/routes.constants';
import { ICheckoutDetail, TDistributorSource } from 'core/model/interfaces/checkout.interface';
import { checkoutService } from 'core/services/checkout/checkout.service';
import type { TSliceExtraReducer } from 'core/store';
import { authGetCustomerSelector } from 'core/store/auth/selectors';
import { createAppAsyncThunk } from 'core/store/store.utils';
import { handleServiceException } from 'shared/utils/errors.utils';
import { checkoutObservability } from 'shared/utils/observability/checkout.observability';
import {
  checkHasNoCreditsForNonHybrid,
  getCustomerIdFromCheckout,
  getInitialCheckoutParams,
} from '../checkout-state.utils';
import { checkoutInitialState, resetCheckoutLoading, SLICE_NAMESPACE } from '../constants';
import { ICheckoutState } from '../types';

export const refetchCheckoutDetailAction = createAppAsyncThunk<
  { detail: ICheckoutDetail; hasNoCreditsForNonHybrid: boolean },
  TDistributorSource
>(`${SLICE_NAMESPACE}/refetchCheckoutDetail`, async (distributor, { getState, rejectWithValue }) => {
  const state = getState();
  const { checkout } = state;
  const customer = authGetCustomerSelector(state);
  const { source: customerSource } = customer;
  try {
    const customerId = getCustomerIdFromCheckout(checkout);

    const params = getInitialCheckoutParams({
      checkoutDetail: checkout.detail,
      checkoutPaymentMethod: checkout.paymentMethod,
      checkoutAddress: checkout.address,
      customerSource,
    });

    const detail = await checkoutService.getCheckoutDetail(customerId, customerSource, params);
    const hasNoCreditsForNonHybrid = checkHasNoCreditsForNonHybrid(detail, customerSource);

    return { detail, hasNoCreditsForNonHybrid };
  } catch (e) {
    const error = handleServiceException(e);
    checkoutObservability.saveCheckoutError({
      step: ROUTES.CHECKOUT_PURCHASE,
      details: checkout.detail,
      customerSource,
      error,
    });
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw rejectWithValue(error);
  }
});

export const refetchCheckoutDetailReducer: TSliceExtraReducer<ICheckoutState> = builder => {
  builder
    .addCase(refetchCheckoutDetailAction.pending, (state, { meta }) => {
      const distributor = meta.arg;
      state.error.global = null;
      state.loading[distributor] = true;
      state.error[distributor] = null;
    })
    .addCase(refetchCheckoutDetailAction.fulfilled, (state, { payload }) => {
      const { detail, hasNoCreditsForNonHybrid } = payload;
      state.error = checkoutInitialState.error;
      state.loading = resetCheckoutLoading;
      state.detail = detail;
      state.hasNoCreditsForNonHybrid = hasNoCreditsForNonHybrid;
    })
    .addCase(refetchCheckoutDetailAction.rejected, (state, { error, meta }) => {
      state.loading = resetCheckoutLoading;
      state.error[meta.arg] = error;
    });

  return builder;
};
